/**
 * TimeSlotPicker — the one hour control in the product.
 *
 * Phone: a field plus a horizontal chip strip (30- or 60-minute slots).
 * Desktop and tablet: that strip is a broken carousel, so the same field
 * opens a two-column menu (AppModal, never RN Modal) with 44pt rows and
 * keyboard movement. Pickup and return stay two fields; the primitive does
 * not paint two giant pill rows on a wide canvas.
 *
 * `minTime` / `maxTime` grey slots outside the day's window. They do not
 * validate `value`: a parent that still holds an illegal time would show it
 * as selected and coral. Booking clamps before it gets here. This component
 * still refuses to paint an out-of-range value as selected, so a missed
 * clamp cannot lie for a frame.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform, ScrollView, Text, useWindowDimensions, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { selection } from '@/lib/utils/haptics'
import { Check, ChevronDown, Clock, X } from '@/components/ui/icons'
import { Pressable } from './Pressable'
import { AppModal } from './AppModal'
import { GlassSheet } from './GlassSheet'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import { cn } from '../../lib/theme/variants'
import {
  TIME_SLOTS,
  HOURLY_TIME_SLOTS,
  formatTime12h,
  isTimeInWindow,
  timePickerScrollSlot,
  timeSlotPickerUsesMenu,
  splitTimeSlotsByMeridiem,
  stepTimeSlotKeyboard,
  type TimeSlotKeyboardDirection,
} from '../../lib/utils/time-slots'

export interface TimeSlotPickerProps {
  /** Selected time ("HH:MM" 24h format or null) */
  value: string | null
  /** Called with "HH:MM" string */
  onChange: (time: string) => void
  /** Tours use full hours; rental pickers keep the default half-hour slots. */
  intervalMinutes?: 30 | 60
  /** Field label */
  label?: string
  /** Placeholder when no time selected */
  placeholder?: string
  /** Error message */
  error?: string
  /**
   * Persistent reason the day is not a full clock — lives under the field,
   * next to the hours it explains, not in a toast and not under clipped chips.
   */
  hint?: string
  /** Disabled state */
  disabled?: boolean
  /** Additional NativeWind className */
  className?: string
  /** E2E selector prefix for the field and time chips */
  testID?: string
  /**
   * La hora mas temprana que se puede elegir ese dia, en "HH:MM" 24h.
   *
   * Existe por el colchon de cambio. El calendario dejo de bloquear el dia de la
   * devolucion --eso recupera un dia de inventario por renta-- pero ese dia no
   * esta libre entero: si la renta anterior termina a las 10:00 y el colchon es
   * de 3 h, el carro se entrega desde las 13:00. Sin este tope, la pantalla
   * ofreceria las 48 horas y el servidor rechazaria las primeras, que es el
   * callejon sin salida de siempre movido de lugar.
   *
   * `undefined` = el dia esta libre entero.
   */
  minTime?: string
  /** Igual que `minTime` pero por el otro extremo del dia. */
  maxTime?: string
}

function slotTestID(testID: string | undefined, time: string): string | undefined {
  return testID ? `${testID}.slot-${time.replace(':', '-')}` : undefined
}

export function TimeSlotPicker({
  value,
  onChange,
  intervalMinutes = 30,
  label,
  placeholder,
  error,
  hint,
  disabled = false,
  minTime,
  maxTime,
  className,
  testID,
}: TimeSlotPickerProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { box } = useFontScale()
  const { isDesktop, isTablet } = useBreakpoint()
  const usesMenu = timeSlotPickerUsesMenu(isDesktop, isTablet)
  const { height: windowHeight } = useWindowDimensions()
  const scrollRef = useRef<ScrollView>(null)
  const menuScrollRef = useRef<ScrollView>(null)
  const slotLayoutsRef = useRef(new Map<string, { x: number; width: number }>())
  const rowLayoutsRef = useRef(new Map<number, number>())
  const timeSlots = intervalMinutes === 60 ? HOURLY_TIME_SLOTS : TIME_SLOTS
  const resolvedPlaceholder = placeholder ?? t('ui.select_time')

  const valueInWindow = Boolean(value && isTimeInWindow(value, minTime, maxTime))
  const displayValue = valueInWindow && value ? formatTime12h(value) : ''

  const scrollSlot = timePickerScrollSlot(value, minTime, maxTime, timeSlots)
  const [stripLayoutWidth, setStripLayoutWidth] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [focusedTime, setFocusedTime] = useState<string | null>(null)
  const focusedTimeRef = useRef<string | null>(null)

  const { am, pm } = useMemo(() => splitTimeSlotsByMeridiem(timeSlots), [timeSlots])
  const rowCount = Math.max(am.length, pm.length)
  const caption = error ?? hint

  const centerSelectedSlot = useCallback(() => {
    if (!scrollSlot || !scrollRef.current || stripLayoutWidth <= 0) return
    const layout = slotLayoutsRef.current.get(scrollSlot)
    if (!layout) return

    // Labels such as 12:00 AM and 9:00 AM have different measured widths,
    // especially with Dynamic Type. Center from the rendered geometry so the
    // current choice never opens half-clipped at the edge of the strip.
    const offset = Math.max(0, layout.x + layout.width / 2 - stripLayoutWidth / 2)
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ x: offset, animated: false })
    })
  }, [scrollSlot, stripLayoutWidth])

  useEffect(() => {
    if (usesMenu) return
    centerSelectedSlot()
  }, [centerSelectedSlot, usesMenu])

  const handleSelect = useCallback(
    (time: string) => {
      if (disabled) return
      selection()
      onChange(time)
      if (usesMenu) {
        requestAnimationFrame(() => {
          setIsOpen(false)
        })
      }
    },
    [disabled, onChange, usesMenu],
  )

  const handleOpen = useCallback(() => {
    if (disabled || !usesMenu) return
    const nextFocus = valueInWindow && value ? value : scrollSlot
    focusedTimeRef.current = nextFocus
    setFocusedTime(nextFocus)
    setIsOpen(true)
  }, [disabled, usesMenu, value, valueInWindow, scrollSlot])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (!usesMenu || !isOpen || Platform.OS !== 'web') return undefined
    if (typeof window === 'undefined') return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsOpen(false)
        return
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        const current = focusedTimeRef.current
        if (current && isTimeInWindow(current, minTime, maxTime)) {
          handleSelect(current)
        }
        return
      }
      const directions: Record<string, TimeSlotKeyboardDirection> = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
        Home: 'home',
        End: 'end',
      }
      const direction = directions[event.key]
      if (!direction) return
      event.preventDefault()
      const next = stepTimeSlotKeyboard(
        timeSlots,
        focusedTimeRef.current,
        direction,
        minTime,
        maxTime,
      )
      focusedTimeRef.current = next
      setFocusedTime(next)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleSelect, isOpen, maxTime, minTime, timeSlots, usesMenu])

  useEffect(() => {
    if (!isOpen || !focusedTime) return
    const row = am.includes(focusedTime) ? am.indexOf(focusedTime) : pm.indexOf(focusedTime)
    if (row < 0) return
    const y = rowLayoutsRef.current.get(row)
    if (y == null) return
    menuScrollRef.current?.scrollTo({ y: Math.max(0, y - SPACING.lg), animated: false })
  }, [am, focusedTime, isOpen, pm])

  const fieldLabel = label
    ? `${label}, ${displayValue || resolvedPlaceholder}`
    : displayValue || resolvedPlaceholder

  const fieldChrome = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    minHeight: box(INTERACTION.fieldHeight),
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: error ? colors.error : colors.border,
    backgroundColor: colors.surfaceSubtle,
    opacity: disabled ? 0.4 : 1,
  }

  const renderSlotCell = (time: string | undefined) => {
    if (!time) {
      return <View style={{ flex: 1, minHeight: box(INTERACTION.minTouchTarget) }} />
    }
    const outOfRange = !isTimeInWindow(time, minTime, maxTime)
    const isSelected = time === value && !outOfRange
    const isFocused = time === focusedTime
    const unusable = disabled || outOfRange
    return (
      <Pressable
        key={time}
        onPress={() => {
          if (outOfRange) return
          handleSelect(time)
        }}
        accessibilityRole="menuitem"
        accessibilityLabel={
          outOfRange
            ? t('booking.time_slot_unavailable', { time: formatTime12h(time) })
            : formatTime12h(time)
        }
        accessibilityState={{ selected: isSelected, disabled: unusable }}
        testID={slotTestID(testID, time)}
        style={{
          flex: 1,
          minHeight: box(INTERACTION.minTouchTarget),
          paddingHorizontal: SPACING.md,
          borderRadius: RADIUS.sm,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING.sm,
          backgroundColor: isSelected
            ? COLORS.coral.DEFAULT
            : isFocused
              ? colors.surfaceSubtle
              : 'transparent',
        }}
      >
        <Text
          style={{
            fontSize: 15,
            fontFamily: FONTS.body,
            color: isSelected ? '#FFFFFF' : outOfRange ? colors.textDisabled : colors.textPrimary,
          }}
          numberOfLines={1}
        >
          {formatTime12h(time)}
        </Text>
        {isSelected ? <Check size={16} color="#FFFFFF" strokeWidth={2.5} /> : null}
      </Pressable>
    )
  }

  return (
    <View className={cn(className)}>
      {label ? (
        <Text
          style={{
            fontSize: 14,
            fontFamily: FONTS.body,
            color: colors.textPrimary,
            opacity: 0.75,
            marginBottom: SPACING.md,
            letterSpacing: 0.2,
          }}
        >
          {label}
        </Text>
      ) : null}

      {usesMenu ? (
        <Pressable
          onPress={handleOpen}
          disabled={disabled}
          testID={testID}
          accessibilityRole="button"
          accessibilityLabel={fieldLabel}
          accessibilityState={{ disabled, expanded: isOpen }}
          style={({ pressed }) => ({
            ...fieldChrome,
            opacity: disabled ? 0.4 : pressed ? 0.76 : 1,
            marginBottom: caption ? SPACING.sm : 0,
          })}
        >
          <Clock
            size={18}
            color={colors.textSecondary}
            style={{ marginRight: INTERACTION.fieldIconGap }}
          />
          <Text
            style={{
              flex: 1,
              fontSize: 15,
              fontFamily: FONTS.body,
              color: displayValue ? colors.textPrimary : colors.textMuted,
            }}
            numberOfLines={1}
          >
            {displayValue || resolvedPlaceholder}
          </Text>
          <ChevronDown size={18} color={colors.textSecondary} />
        </Pressable>
      ) : (
        <View
          testID={testID}
          accessible
          accessibilityRole="text"
          accessibilityLabel={fieldLabel}
          style={{
            ...fieldChrome,
            marginBottom: SPACING.sm,
          }}
        >
          <Clock
            size={18}
            color={colors.textSecondary}
            style={{ marginRight: INTERACTION.fieldIconGap }}
          />
          <Text
            style={{
              flex: 1,
              fontSize: 15,
              fontFamily: FONTS.body,
              color: displayValue ? colors.textPrimary : colors.textMuted,
            }}
            numberOfLines={1}
          >
            {displayValue || resolvedPlaceholder}
          </Text>
        </View>
      )}

      {caption ? (
        <Text
          testID={testID ? `${testID}.${error ? 'error' : 'window-hint'}` : undefined}
          accessibilityLiveRegion="polite"
          style={{
            fontSize: 13,
            lineHeight: 18,
            fontFamily: FONTS.body,
            color: error ? colors.textError : colors.textSecondary,
            marginBottom: usesMenu ? 0 : SPACING.sm,
          }}
        >
          {caption}
        </Text>
      ) : null}

      {usesMenu ? null : (
        <ScrollView
          ref={scrollRef}
          horizontal
          onLayout={(e) => {
            setStripLayoutWidth(e.nativeEvent.layout.width)
          }}
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          scrollEventThrottle={16}
          decelerationRate="fast"
          contentContainerStyle={{
            paddingHorizontal: SPACING.xs,
            gap: SPACING.sm,
            paddingVertical: SPACING.xs,
          }}
          style={{ marginHorizontal: -SPACING.xs }}
        >
          {timeSlots.map((time) => {
            const outOfRange = !isTimeInWindow(time, minTime, maxTime)
            // An illegal value must not read as the current choice. Coral on a
            // greyed chip is the same lie the parent clamp exists to stop.
            const isSelected = time === value && !outOfRange
            const unusable = disabled || outOfRange
            return (
              <Pressable
                key={time}
                onLayout={(event) => {
                  const { x, width } = event.nativeEvent.layout
                  slotLayoutsRef.current.set(time, { x, width })
                  if (time === scrollSlot) centerSelectedSlot()
                }}
                onPress={() => {
                  if (outOfRange) return
                  handleSelect(time)
                }}
                accessibilityRole="button"
                accessibilityLabel={
                  outOfRange
                    ? t('booking.time_slot_unavailable', { time: formatTime12h(time) })
                    : formatTime12h(time)
                }
                accessibilityState={{ selected: isSelected, disabled: unusable }}
                testID={slotTestID(testID, time)}
                style={{
                  /*
                   * 44pt DE VERDAD, no 36.
                   *
                   * `paddingVertical: SPACING.sm` (8) con texto de 13 daba un chip
                   * de ~36pt, por debajo del minimo que exige la propia guia del
                   * repo. Y estos no son un adorno: hay que elegir hora de
                   * recogida Y de devolucion para poder continuar la reserva, en
                   * dos tiras de 48 chips cada una. Un dedo que cae entre dos no
                   * elige ninguna, y el boton de Continuar se queda gris sin
                   * decir por que.
                   *
                   * `minHeight` en vez de mas padding: el chip crece hasta 44 y el
                   * texto queda centrado, sin tocar el ritmo horizontal.
                   */
                  minHeight: box(INTERACTION.minTouchTarget),
                  justifyContent: 'center',
                  paddingVertical: SPACING.sm,
                  paddingHorizontal: SPACING.md,
                  borderRadius: RADIUS.full,
                  backgroundColor: isSelected ? COLORS.coral.DEFAULT : colors.surface,
                  borderWidth: 1,
                  borderColor: isSelected ? COLORS.coral.DEFAULT : colors.border,
                  ...(Platform.OS === 'web' && !isSelected && !outOfRange ? WEB_SHADOWS.sm : {}),
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    fontFamily: isSelected ? FONTS.bodyBold : FONTS.body,
                    color: isSelected
                      ? '#FFFFFF'
                      : outOfRange
                        ? colors.textDisabled
                        : colors.textPrimary,
                  }}
                  numberOfLines={1}
                >
                  {formatTime12h(time)}
                </Text>
              </Pressable>
            )
          })}
        </ScrollView>
      )}

      {usesMenu ? (
        <AppModal
          visible={isOpen}
          transparent
          animationType="fade"
          onRequestClose={handleClose}
          presentationStyle="overFullScreen"
          statusBarTranslucent
        >
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              padding: SPACING.lg,
            }}
          >
            <Pressable
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
            />
            <GlassSheet
              testID={testID ? `${testID}.menu` : undefined}
              accessibilityRole="menu"
              style={{
                width: '100%',
                maxWidth: 420,
                maxHeight: Math.min(windowHeight * 0.72, 520),
              }}
              contentStyle={{
                paddingTop: SPACING.md,
                paddingBottom: SPACING.md,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: SPACING.md,
                  paddingBottom: SPACING.sm,
                  gap: SPACING.sm,
                  minHeight: box(INTERACTION.minTouchTarget),
                }}
              >
                <Text
                  testID={testID ? `${testID}.menu-title` : undefined}
                  style={{
                    flex: 1,
                    fontSize: 17,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                  numberOfLines={1}
                >
                  {label || resolvedPlaceholder}
                </Text>
                <Pressable
                  onPress={handleClose}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.close')}
                  hitSlop={INTERACTION.iconHitSlop}
                  style={{
                    width: INTERACTION.minTouchTarget,
                    height: INTERACTION.minTouchTarget,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X size={18} color={colors.textSecondary} />
                </Pressable>
              </View>

              {caption ? (
                <Text
                  style={{
                    fontSize: 13,
                    lineHeight: 18,
                    fontFamily: FONTS.body,
                    color: error ? colors.textError : colors.textSecondary,
                    paddingHorizontal: SPACING.md,
                    paddingBottom: SPACING.sm,
                  }}
                >
                  {caption}
                </Text>
              ) : null}

              <View
                style={{
                  flexDirection: 'row',
                  paddingHorizontal: SPACING.sm,
                  paddingBottom: SPACING.xs,
                }}
              >
                <Text
                  style={{
                    flex: 1,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                    paddingHorizontal: SPACING.md,
                  }}
                >
                  {t('ui.time_period_am')}
                </Text>
                <Text
                  style={{
                    flex: 1,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                    paddingHorizontal: SPACING.md,
                  }}
                >
                  {t('ui.time_period_pm')}
                </Text>
              </View>

              <ScrollView
                ref={menuScrollRef}
                style={{ flexShrink: 1 }}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {Array.from({ length: rowCount }, (_, row) => (
                  <View
                    key={am[row] ?? pm[row] ?? String(row)}
                    onLayout={(event) => {
                      rowLayoutsRef.current.set(row, event.nativeEvent.layout.y)
                    }}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      paddingHorizontal: SPACING.sm,
                      gap: SPACING.xs,
                    }}
                  >
                    {renderSlotCell(am[row])}
                    {renderSlotCell(pm[row])}
                  </View>
                ))}
              </ScrollView>
            </GlassSheet>
          </View>
        </AppModal>
      ) : null}
    </View>
  )
}
